import type { AudioScene } from './audio-scene.js';
import {
  audioSceneProfile,
  musicBarDuration,
  musicLayerCount,
  musicTrackAt,
  nextMusicTrackIndex,
} from './audio-design.js';

export type GameSound =
  | 'select'
  | 'navigate'
  | 'gameStart'
  | 'place'
  | 'clear'
  | 'multiClear'
  | 'clearMany'
  | 'clearCross'
  | 'clearPerfect'
  | 'armorBreak'
  | 'combo2'
  | 'combo3'
  | 'comboBig'
  | 'comboBreak'
  | 'record'
  | 'win'
  | 'lose'
  | 'draw'
  | 'reward'
  | 'rankUp'
  | 'energy'
  | 'purchase'
  | 'gift'
  | 'duelChallenge'
  | 'chat'
  | 'error'
  | 'pathNearGoal'
  | 'pathThreeStar'
  | 'scratch'
  | 'artifactLaser'
  | 'artifactRocket'
  | 'artifactPlasma'
  | 'artifactImpulse'
  | 'artifactReroll'
  | 'artifactUndo';

let placementVariation = 0;
let context: AudioContext | null = null;
let effectsEnabled = true;
let musicEnabled = true;
let scene: AudioScene = 'menu';
let intensity = 0;
let musicTimer: number | null = null;
let musicStep = 0;
let musicTrackIndex = 0;
let musicTrackBar = 0;
let musicGain: GainNode | null = null;
let musicTrackGain: GainNode | null = null;
let effectsGain: GainNode | null = null;
let limiter: DynamicsCompressorNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
const ambientSources = new Set<AudioScheduledSourceNode>();

const audioContext = (): AudioContext | null => {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  context ??= new AudioContext();
  if (!musicGain || !musicTrackGain || !effectsGain || !limiter) {
    musicGain = context.createGain();
    musicTrackGain = context.createGain();
    effectsGain = context.createGain();
    limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -16;
    limiter.knee.value = 18;
    limiter.ratio.value = 5;
    limiter.attack.value = 0.004;
    limiter.release.value = 0.18;
    musicGain.gain.value = musicEnabled ? 1 : 0;
    musicTrackGain.gain.value = 1;
    effectsGain.gain.value = effectsEnabled ? 1 : 0;
    musicTrackGain.connect(musicGain);
    musicGain.connect(limiter);
    effectsGain.connect(limiter);
    limiter.connect(context.destination);
  }
  return context;
};

const tone = (
  frequency: number,
  duration: number,
  volume: number,
  type: OscillatorType = 'sine',
  endFrequency = frequency,
  delay = 0,
): void => {
  if (!effectsEnabled || (typeof document !== 'undefined' && document.hidden)) return;
  const audio = audioContext();
  if (!audio || audio.state !== 'running' || !effectsGain) return;
  const now = audio.currentTime + delay;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  const filter = audio.createBiquadFilter();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), now + duration);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(Math.min(5_800, Math.max(880, frequency * 5)), now);
  filter.Q.value = 0.7;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + Math.min(0.024, duration * 0.2));
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  oscillator.connect(filter).connect(gain).connect(effectsGain);
  oscillator.start(now);
  oscillator.stop(now + duration + 0.02);
};

const synthTone = (
  frequency: number,
  duration: number,
  volume: number,
  type: OscillatorType,
  cutoff: number,
  delay = 0,
  attack = 0.035,
): void => {
  if (!musicEnabled) return;
  const audio = audioContext();
  if (!audio || audio.state !== 'running' || !musicTrackGain) return;
  const now = audio.currentTime + delay;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  const filter = audio.createBiquadFilter();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, now);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(cutoff, now);
  filter.Q.value = 0.8;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + Math.min(attack, duration * 0.4));
  gain.gain.setValueAtTime(volume, now + Math.max(attack, duration * 0.55));
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  oscillator.connect(filter);
  filter.connect(gain).connect(musicTrackGain);
  oscillator.start(now);
  oscillator.stop(now + duration + 0.05);
  ambientSources.add(oscillator);
  oscillator.addEventListener('ended', () => ambientSources.delete(oscillator), { once: true });
};

const createNoiseBuffer = (audio: AudioContext): AudioBuffer => {
  if (noiseBuffer) return noiseBuffer;
  const length = Math.floor(audio.sampleRate * 0.55);
  const buffer = audio.createBuffer(1, length, audio.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 0x51f15e;
  for (let index = 0; index < length; index += 1) {
    seed = (seed * 1_664_525 + 1_013_904_223) >>> 0;
    data[index] = (seed / 0x80000000 - 1) * (1 - index / length);
  }
  noiseBuffer = buffer;
  return buffer;
};

const noise = (
  duration: number,
  volume: number,
  cutoff: number,
  delay = 0,
  bus: 'music' | 'effects' = 'effects',
): void => {
  if (bus === 'music' ? !musicEnabled : !effectsEnabled) return;
  const audio = audioContext();
  const destination = bus === 'music' ? musicTrackGain : effectsGain;
  if (!audio || audio.state !== 'running' || !destination) return;
  const now = audio.currentTime + delay;
  const source = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const gain = audio.createGain();
  source.buffer = createNoiseBuffer(audio);
  filter.type = cutoff > 2_000 ? 'highpass' : 'lowpass';
  filter.frequency.setValueAtTime(cutoff, now);
  filter.Q.value = 0.6;
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  source.connect(filter).connect(gain).connect(destination);
  source.start(now);
  source.stop(now + duration);
  if (bus === 'music') {
    ambientSources.add(source);
    source.addEventListener('ended', () => ambientSources.delete(source), { once: true });
  }
};

const stopMusic = (): void => {
  if (musicTimer !== null) window.clearTimeout(musicTimer);
  musicTimer = null;
  for (const source of ambientSources) {
    try {
      source.stop();
    } catch {
      // The source may already have ended between iteration and stop().
    }
  }
  ambientSources.clear();
};

const scheduleMusic = (delaySeconds: number): void => {
  if (!musicEnabled || document.hidden) return;
  musicTimer = window.setTimeout(playAmbientChord, Math.max(120, delaySeconds * 1_000 - 35));
};

const transitionTrack = (): void => {
  if (!context || !musicTrackGain) return;
  const now = context.currentTime;
  musicTrackGain.gain.cancelScheduledValues(now);
  musicTrackGain.gain.setValueAtTime(Math.max(0.0001, musicTrackGain.gain.value), now);
  musicTrackGain.gain.exponentialRampToValueAtTime(0.34, now + 0.16);
  musicTrackGain.gain.exponentialRampToValueAtTime(1, now + 0.72);
};

function playAmbientChord(): void {
  if (!musicEnabled || document.hidden) return;
  const profile = audioSceneProfile(scene);
  const track = musicTrackAt(musicTrackIndex);
  if (musicTrackBar === 0 && musicStep > 0) transitionTrack();
  const chord = profile.chords[(musicStep + track.chordOffset) % profile.chords.length]!;
  const barDuration = musicBarDuration(scene, intensity, track);
  const beat = barDuration / 4;
  const eighth = beat / 2;
  const layers = musicLayerCount(scene, intensity);
  const cutoff = (profile.cutoff + intensity * 520) * track.brightness;

  chord.forEach((note, index) => {
    synthTone(
      note,
      barDuration + 0.22,
      index === 0 ? 0.0044 : 0.0028,
      profile.padWave,
      cutoff,
      index * 0.018,
      0.48,
    );
    if (index > 0)
      synthTone(note * 2, barDuration * 0.72, 0.0009, 'sine', cutoff * 1.4, 0.08, 0.72);
  });

  for (let index = 0; index < 8; index += 1) {
    const delay = index * eighth;
    const bassIndex = track.bassPattern[index]!;
    if (bassIndex >= 0) {
      synthTone(
        chord[bassIndex % chord.length]! / 2,
        index % 2 === 0 ? beat * 0.62 : eighth * 0.72,
        0.0038 + profile.pulse * 0.0026 + intensity * 0.0014,
        'sine',
        430,
        delay,
        0.007,
      );
    }
    if (track.kickPattern[index]) {
      synthTone(72, 0.12, 0.0048 + profile.pulse * 0.002, 'sine', 240, delay, 0.004);
      noise(0.045, 0.0014, 520, delay, 'music');
    }
    if (layers >= 3 && track.hatPattern[index])
      noise(0.035, 0.0015 + intensity * 0.001, 4_800, delay + 0.012, 'music');

    const profileNote = profile.arp[(index + musicStep) % profile.arp.length]!;
    const trackNote = track.leadPattern[(index + musicTrackBar) % track.leadPattern.length]!;
    const noteIndex = (profileNote + trackNote) % chord.length;
    const note = chord[noteIndex]! * track.leadOctave;
    synthTone(
      note,
      eighth * (0.45 + profile.shimmer * 0.35),
      0.002 + profile.shimmer * 0.0014 + intensity * 0.0008,
      profile.leadWave,
      cutoff * 1.65,
      delay,
      0.008,
    );
  }

  if (layers >= 4) {
    const accentDelay = Math.max(0, barDuration - beat);
    synthTone(chord[2]! * 2, 0.38, 0.0026, 'sine', 2_800, accentDelay, 0.012);
  }
  musicStep += 1;
  musicTrackBar += 1;
  if (musicTrackBar >= track.bars) {
    musicTrackIndex = nextMusicTrackIndex(musicTrackIndex);
    musicTrackBar = 0;
  }
  scheduleMusic(barDuration);
}

const startMusic = (): void => {
  const audio = audioContext();
  if (
    !musicEnabled ||
    musicTimer !== null ||
    !audio ||
    audio.state !== 'running' ||
    document.hidden
  )
    return;
  playAmbientChord();
};

export const unlockAudio = async (): Promise<void> => {
  const audio = audioContext();
  if (!audio || (!effectsEnabled && !musicEnabled)) return;
  if (audio.state !== 'running') await audio.resume().catch(() => undefined);
  startMusic();
};

/** Applies persisted preferences without touching Web Audio before the first user gesture. */
export const configureAudioPreferences = (effects: boolean, music: boolean): void => {
  effectsEnabled = effects;
  musicEnabled = music;
  if (effectsGain && context) effectsGain.gain.value = effects ? 1 : 0;
  if (musicGain && context) musicGain.gain.value = music ? 1 : 0;
  if (!music) stopMusic();
};

export const setEffectsEnabled = (value: boolean): void => {
  effectsEnabled = value;
  if (effectsGain && context)
    effectsGain.gain.setTargetAtTime(value ? 1 : 0, context.currentTime, 0.03);
  if (value) void unlockAudio();
};

export const setMusicEnabled = (value: boolean): void => {
  musicEnabled = value;
  if (musicGain && context)
    musicGain.gain.setTargetAtTime(value ? 1 : 0, context.currentTime, 0.08);
  if (value) void unlockAudio();
  else stopMusic();
};

export const setAudioScene = (value: AudioScene): void => {
  if (scene === value) return;
  scene = value;
  intensity = value === 'arena' || value === 'duel' || value.startsWith('path-') ? intensity : 0;
  musicStep = 0;
  musicTrackBar = 0;
  stopMusic();
  startMusic();
};

export const setMusicIntensity = (value: number): void => {
  const next = Math.min(1, Math.max(0, value));
  if (Math.abs(next - intensity) >= 0.08) intensity = next;
};

export const isAudioEnabled = (): boolean => effectsEnabled || musicEnabled;

const sequence = (
  notes: readonly number[],
  duration: number,
  volume: number,
  gap: number,
  type: OscillatorType = 'sine',
): void => {
  notes.forEach((note, index) => tone(note, duration, volume, type, note * 1.06, index * gap));
};

const softImpact = (frequency: number, volume: number, delay = 0): void => {
  tone(frequency, 0.14, volume, 'sine', frequency * 0.48, delay);
  noise(0.09, volume * 0.38, 780, delay);
};

const shimmer = (notes: readonly number[], volume: number, delay = 0): void => {
  notes.forEach((note, index) => {
    tone(note, 0.24, volume, 'sine', note * 1.015, delay + index * 0.052);
    tone(note * 2, 0.12, volume * 0.24, 'triangle', note * 2.04, delay + index * 0.052);
  });
};

export const playGameSound = (sound: GameSound): void => {
  if (!effectsEnabled || (typeof document !== 'undefined' && document.hidden)) return;
  const audio = audioContext();
  if (!audio) return;
  if (audio.state !== 'running') {
    void unlockAudio().then(() => {
      if (context?.state === 'running') playGameSound(sound);
    });
    return;
  }
  if (musicEnabled && musicGain) {
    const now = audio.currentTime;
    musicGain.gain.cancelScheduledValues(now);
    musicGain.gain.setTargetAtTime(0.58, now, 0.025);
    musicGain.gain.setTargetAtTime(1, now + 0.32, 0.16);
  }
  if (sound === 'select') tone(330, 0.09, 0.035, 'sine', 392);
  if (sound === 'navigate') tone(246.94, 0.08, 0.022, 'sine', 293.66);
  if (sound === 'gameStart') sequence([196, 293.66, 392], 0.18, 0.03, 0.07, 'triangle');
  if (sound === 'place') {
    const frequency = [138.59, 146.83, 155.56][placementVariation++ % 3] ?? 146.83;
    softImpact(frequency, 0.03);
    tone(293.66, 0.1, 0.02, 'sine', 329.63, 0.018);
  }
  if (sound === 'clear') {
    noise(0.18, 0.018, 3_200);
    shimmer([261.63, 392, 523.25], 0.022, 0.025);
  }
  if (sound === 'multiClear') {
    softImpact(98, 0.034);
    noise(0.24, 0.021, 3_800, 0.025);
    shimmer([261.63, 329.63, 392, 523.25], 0.024, 0.035);
  }
  if (sound === 'clearMany' || sound === 'clearCross') {
    softImpact(sound === 'clearCross' ? 73.42 : 98, 0.035);
    noise(0.28, 0.019, 4200, 0.02);
    shimmer(
      sound === 'clearCross' ? [196, 392, 783.99] : [261.63, 392, 523.25, 783.99],
      0.025,
      0.03,
    );
  }
  if (sound === 'clearPerfect') {
    softImpact(130.81, 0.03);
    shimmer([261.63, 329.63, 392, 523.25, 1046.5], 0.027, 0.03);
  }
  if (sound === 'armorBreak') {
    noise(0.14, 0.03, 1600);
    softImpact(82.41, 0.026);
  }
  if (sound === 'combo2') sequence([392, 523.25], 0.18, 0.035, 0.055);
  if (sound === 'combo3') sequence([392, 493.88, 659.25], 0.2, 0.036, 0.048, 'triangle');
  if (sound === 'comboBig') sequence([261.63, 392, 523.25, 783.99], 0.3, 0.04, 0.042);
  if (sound === 'comboBreak') sequence([293.66, 220, 164.81], 0.12, 0.024, 0.045, 'triangle');
  if (sound === 'record' || sound === 'rankUp')
    sequence([329.63, 493.88, 659.25, 987.77], 0.34, 0.042, 0.075, 'triangle');
  if (sound === 'win' || sound === 'reward')
    sequence([261.63, 329.63, 392, 523.25], 0.3, 0.037, 0.07);
  if (sound === 'lose') sequence([220, 164.81, 110], 0.3, 0.034, 0.085, 'triangle');
  if (sound === 'draw') sequence([220, 293.66, 220], 0.24, 0.028, 0.08);
  if (sound === 'energy') sequence([293.66, 440, 587.33], 0.22, 0.03, 0.085);
  if (sound === 'purchase') sequence([523.25, 659.25, 783.99], 0.18, 0.032, 0.055, 'triangle');
  if (sound === 'gift') sequence([392, 523.25, 659.25, 880], 0.25, 0.034, 0.065);
  if (sound === 'duelChallenge') {
    tone(110, 0.34, 0.035, 'sawtooth', 164.81);
    tone(220, 0.18, 0.026, 'triangle', 293.66, 0.13);
  }
  if (sound === 'chat') sequence([659.25, 783.99], 0.11, 0.02, 0.06);
  if (sound === 'error') tone(130.81, 0.18, 0.045, 'sawtooth', 82.41);
  if (sound === 'pathNearGoal') {
    shimmer([392, 493.88, 587.33], 0.018);
    tone(196, 0.42, 0.016, 'sine', 293.66, 0.08);
  }
  if (sound === 'pathThreeStar') {
    noise(0.32, 0.012, 4_200, 0.04);
    shimmer([392, 523.25, 659.25, 783.99, 1_046.5], 0.026);
  }
  if (sound === 'scratch') {
    noise(0.085, 0.025, 1_700);
    noise(0.06, 0.008, 3_800, 0.012);
  }
  if (sound === 'artifactLaser') {
    noise(0.22, 0.016, 4_600);
    tone(1_760, 0.26, 0.022, 'triangle', 246.94);
    tone(880, 0.13, 0.012, 'sine', 1_760, 0.025);
  }
  if (sound === 'artifactRocket') {
    tone(220, 0.18, 0.018, 'triangle', 73.42);
    softImpact(73.42, 0.052, 0.11);
    noise(0.34, 0.034, 640, 0.1);
  }
  if (sound === 'artifactPlasma') {
    shimmer([523.25, 783.99, 1_174.66], 0.017);
    tone(110, 0.44, 0.026, 'sine', 440);
    noise(0.28, 0.012, 4_800, 0.08);
  }
  if (sound === 'artifactImpulse') {
    softImpact(65.41, 0.058);
    noise(0.26, 0.026, 920);
    shimmer([220, 440, 880], 0.018, 0.035);
  }
  if (sound === 'artifactReroll') {
    sequence([293.66, 392, 523.25, 659.25], 0.1, 0.016, 0.045, 'sine');
    noise(0.22, 0.009, 3_600, 0.02);
  }
  if (sound === 'artifactUndo') {
    sequence([659.25, 493.88, 392, 293.66], 0.16, 0.017, 0.042, 'sine');
    tone(196, 0.36, 0.011, 'triangle', 392, 0.04);
  }
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopMusic();
    else if (context) void unlockAudio();
  });
}
